/**
 * Aircraft Form Schema
 *
 * Shared Zod validation schema for all aircraft forms.
 * Provides partial schemas for quick-edit variants.
 */

import { z } from 'zod'
import { AIRCRAFT_STATUSES } from '@/types/aircrafts'

/**
 * Full aircraft form schema - used by AircraftMutateDrawer
 */
export const aircraftFormSchema = z.object({
  // Basic Info
  tail_number: z.string().min(1, 'Tail number is required').max(20),
  aircraft_type_id: z.string().nullable(),
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  status: z.enum(AIRCRAFT_STATUSES),

  // Ownership
  owner_client_id: z.string().nullable(),
  operator_client_id: z.string().nullable(),
  owner_name: z.string().nullable(),

  // Dimensions & Weight
  length: z.number().nullable(),
  wingspan: z.number().nullable(),
  height: z.number().nullable(),
  tail_height: z.number().nullable(),
  empty_weight: z.number().nullable(),
  max_takeoff_weight: z.number().nullable(),

  // Registration & Compliance
  registration_country: z.string().nullable(),
  serial_number: z.string().nullable(),
  year_manufactured: z.number().nullable(),
  annual_inspection_due: z.string().nullable(),
  insurance_expiry: z.string().nullable(),

  // Location
  home_location_id: z.string().nullable(),
  current_location_id: z.string().nullable(),
  current_area_id: z.string().nullable(),

  // 3D Model
  custom_model_file_path: z.string().nullable(),
  custom_model_scale: z.number().nullable(),

  // Notes
  notes: z.string().nullable(),
})

export type AircraftFormInput = z.input<typeof aircraftFormSchema>
export type AircraftFormValues = z.output<typeof aircraftFormSchema>

/**
 * Default values for full form
 */
export const defaultAircraftFormValues: AircraftFormValues = {
  tail_number: '',
  aircraft_type_id: null,
  manufacturer: null,
  model: null,
  status: 'active',
  owner_client_id: null,
  operator_client_id: null,
  owner_name: null,
  length: null,
  wingspan: null,
  height: null,
  tail_height: null,
  empty_weight: null,
  max_takeoff_weight: null,
  registration_country: null,
  serial_number: null,
  year_manufactured: null,
  annual_inspection_due: null,
  insurance_expiry: null,
  home_location_id: null,
  current_location_id: null,
  current_area_id: null,
  custom_model_file_path: null,
  custom_model_scale: null,
  notes: null,
}

/**
 * Details variant schema - for quick edit in hangar view
 * Includes: manufacturer, model, serial, year, dimensions, compliance dates
 */
export const aircraftDetailsSchema = aircraftFormSchema.pick({
  manufacturer: true,
  model: true,
  serial_number: true,
  year_manufactured: true,
  wingspan: true,
  length: true,
  height: true,
  insurance_expiry: true,
  annual_inspection_due: true,
})

export type AircraftDetailsInput = z.input<typeof aircraftDetailsSchema>
export type AircraftDetailsValues = z.output<typeof aircraftDetailsSchema>

/**
 * Notes variant schema - for quick notes edit
 */
export const aircraftNotesSchema = aircraftFormSchema.pick({
  notes: true,
})

export type AircraftNotesInput = z.input<typeof aircraftNotesSchema>
export type AircraftNotesValues = z.output<typeof aircraftNotesSchema>
